import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

const API_BASE = '/api/v1';

interface AdminProfile {
  id: string;
  email: string;
  username: string;
  role: string;
}

export const useAdminAuthStore = defineStore('adminAuth', () => {
  const accessToken = ref<string | null>(localStorage.getItem('admin_access_token'));
  const admin = ref<AdminProfile | null>(
    JSON.parse(localStorage.getItem('admin_profile') ?? 'null') as AdminProfile | null,
  );
  const loading = ref(false);

  const isAuthenticated = computed(() => !!accessToken.value);

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    try {
      const res = await fetch(`${API_BASE}/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Login failed');

      accessToken.value = json.data.accessToken;
      admin.value = json.data.admin;
      localStorage.setItem('admin_access_token', json.data.accessToken);
      localStorage.setItem('admin_profile', JSON.stringify(json.data.admin));
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      const token = accessToken.value;
      if (token) {
        await fetch(`${API_BASE}/admin/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      }
    } catch {
      // ignore logout API errors
    } finally {
      clearSession();
    }
  }

  function clearSession() {
    accessToken.value = null;
    admin.value = null;
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_profile');
  }

  return {
    accessToken,
    admin,
    loading,
    isAuthenticated,
    login,
    logout,
    clearSession,
  };
});
